import { Button } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { EmptyState } from '../empty_state/EmptyState';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	brandsRestCrudThunksForBuyer,
	brandsRestCrudThunksForSupplier,
} from '../../thunks/brands_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { BRANDS_CRUD_ACTION_CREATORS } from '../../actions/brands_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import BrandLogoDisplay from './BrandLogoDisplay';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import AddOrEditBrandPopup from './AddOrEditBrandPopup';
import { nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import posthog from 'posthog-js';
import { BRAND_ASSET_GROUP_FEATURE_FLAG } from '../../utils/LocationUtils';

const TC_NAME = 'brandsIndex';

export const BrandDisplay: FC<any> = ({ brand, assetGroupEnabled }): React.ReactElement => {
	return (
		<div className="locationsIndexPage__row">
			<div>
				<div className="flex flex-row items-center">
					<BrandLogoDisplay brand={brand} showPlaceholder />
					<div className="flex flex-col gap-1">
						<div className="h4medium text-grey-8">{brand.brandName}</div>
						{assetGroupEnabled ? (
							<div className="bodyregular text-character-secondary">{`Asset Groups: ${nullSafeGetOrElse(
								'assetGroupCount',
								brand,
								0
							)}`}</div>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

const BrandsOverviewIndexPage: FC<any> = ({
	brands,
	userType,
	updateFilters,
	clearAndUpdateFilters,
	history,
}): React.ReactElement => {
	const [brandPopUpVisible, setBrandPopUpVisible] = useState(false);
	const [editingBrand, setEditingBrand] = useState(null);

	const assetGroupEnabled = posthog.isFeatureEnabled(BRAND_ASSET_GROUP_FEATURE_FLAG);

	const _showBrandPopup = useCallback(() => {
		setEditingBrand(null);
		setBrandPopUpVisible(true);
	}, []);

	const _hideBrandPopup = useCallback(() => {
		setBrandPopUpVisible(false);
		setEditingBrand(null);
	}, []);

	const onBrandEdit = useCallback(
		(record) => (e) => {
			stopDefaultEvents(e);
			setEditingBrand(record);
			setBrandPopUpVisible(true);
		},
		[]
	);

	const column = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'brandName',
				defaultSortOrder: 'ascend',
				render: (text, record) => (
					<div className="flex flex-row items-center justify-between">
						<BrandDisplay brand={record} assetGroupEnabled={assetGroupEnabled} />
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
							<Button onClick={onBrandEdit(record)} type="ghost" size="large">
								Edit
							</Button>
						</AccessPermissionChecker>
					</div>
				),
			},
		],
		[assetGroupEnabled, onBrandEdit]
	);

	const createNewButton = useMemo(
		() => (
			<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
				<Button style={{ marginLeft: 16 }} type="primary" size="large" onClick={_showBrandPopup}>
					New Brand
				</Button>
			</AccessPermissionChecker>
		),
		[_showBrandPopup]
	);

	const onTableRow = useCallback(
		(record) => ({
			onClick: () => history.push(`/${userType}/locations/overview/brands/detail/${record.id}`),
			style: { cursor: 'pointer' },
		}),
		[history, userType]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={brands}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={[]}
				entityCollectionName="brands"
				tableColumns={column}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? brandsRestCrudThunksForSupplier.read
						: brandsRestCrudThunksForBuyer.read
				}
				rightActions={<div>{createNewButton}</div>}
				showHeader={false}
				hideSearch
				onTableRow={assetGroupEnabled ? onTableRow : undefined}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Looks like your team has not added any brands yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any brands yet.
								</div>
								{createNewButton}
							</div>
						}
					/>
				}
			/>
			{brandPopUpVisible && (
				<AddOrEditBrandPopup
					formData={editingBrand}
					onCancel={_hideBrandPopup}
					targetCollectionName={TC_NAME}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	brands: state.brands,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(BRANDS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(BRANDS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(BrandsOverviewIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
